/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) 2022 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import NavElements from 'flarum/newvigation/components/NavElements';
import LinkButton from 'flarum/common/components/LinkButton';
import Separator from 'flarum/common/components/Separator';
import SessionDropdown from 'flarum/forum/components/SessionDropdown';
import type ItemList from 'flarum/common/utils/ItemList';
import type Mithril from 'mithril';
import type Tag from 'flarum/tags/common/models/Tag';

/**
 * The slice of `glowingblue/newvigation`'s `NavElements` used here.
 */
export type NavElementsLike = {
	items(): ItemList<Mithril.Children>;
	mobileItems(): ItemList<Mithril.Children>;
};

let privateDiscussionsLink: Mithril.Children;
let followingLink: Mithril.Children;
let settingsLink: Mithril.Children;

export default function () {
	if (app.initializers.has('glowingblue-newvigation')) {
		extend(NavElements.prototype as NavElementsLike, 'items', function (items: ItemList<Mithril.Children>) {
			// Add link to home page
			items.add(
				'home',
				<LinkButton href={app.route('home')} icon="far fa-home">
					{app.translator.trans('glowingblue-orell-thalia-common.forum.home.nav')}
				</LinkButton>,
				250
			);

			// Add link to `leserunde` tag
			// First check that the tag exists
			const tag = app.store.all<Tag>('tags').find((tag) => tag.slug() === 'leserunden');
			if (tag) {
				items.add(
					'leserunde',
					<LinkButton href={app.route.tag(tag)} icon="far fa-book-heart">
						{tag.name()}
					</LinkButton>,
					140
				);
			}

			// Remove link to tags page
			items.remove('tags');

			// Remove following, and save for reuse in SessionDropdown
			if (items.has('following')) {
				followingLink = items.get('following');
				items.remove('following');
			}

			// Remove private discussions, and save for reuse in SessionDropdown
			if (items.has('privateDiscussions')) {
				privateDiscussionsLink = items.get('privateDiscussions');
				items.remove('privateDiscussions');
			}
		});
	}

	extend(SessionDropdown.prototype, 'items', (items) => {
		if (followingLink) {
			items.add('following', followingLink, 98);
		}
		if (privateDiscussionsLink) {
			items.add('privateDiscussions', privateDiscussionsLink, 97);
		}

		if (items.has('administration')) {
			items.add('adminSeparator', <Separator />, 1);
		}

		// Save the settings link, so that in can be added to the mobileItems of newvigation
		settingsLink = items.get('settings');
	});

	if (app.initializers.has('glowingblue-newvigation')) {
		extend(NavElements.prototype as NavElementsLike, 'mobileItems', function (items: ItemList<Mithril.Children>) {
			if (settingsLink) {
				items.add('settings', settingsLink, 49);
			}
		});
	}
}
